import React, { useState } from 'react';
import { Download, X } from 'lucide-react';
import Select from 'react-select'; // High-quality multi-select
import './ExportProject.css';
import { baseurl } from '../../baseurl';
import axios from 'axios';
import Swal from 'sweetalert2';

const ExportProject = ({ selectedIds = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // State for form data
  const [exportFormat, setExportFormat] = useState('CSV');
  const [exportRange, setExportRange] = useState('All Projects');
  const [selectedColumns, setSelectedColumns] = useState([]);

  const toggleModal = () => setIsOpen(!isOpen);

  // Core Column Options
  const columnOptions = [
    { value: 'id', label: 'ID' }, 
    { value: 'property_id', label: 'Property ID' },
    { value: 'address', label: 'Address' },
    { value: 'lot_area', label: 'Lot Area' },
    { value: 'report_type', label: 'Report Type' },
    { value: 'status', label: 'Status' },
    { value: 'created_at', label: 'Created At' },
    { value: 'complete_at', label: 'Completed At' }
  ];

  // Prepend a Select All option to the select list dropdown
  const selectOptions = [
    { value: 'all', label: 'Select All Columns' },
    ...columnOptions
  ];

  // Intercept the change to handle Select All functionality
  const handleColumnChange = (selected) => {
    // If the user selected 'Select All', check all baseline columns instead
    if (selected && selected.some(option => option.value === 'all')) {
      setSelectedColumns(columnOptions);
    } else {
      setSelectedColumns(selected || []);
    }
  };

  // Custom styles for React-Select to match your UI
  const customSelectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: '8px',
      borderColor: '#e2e8f0',
      padding: '2px',
      boxShadow: 'none',
      '&:hover': { borderColor: '#cbd5e1' }
    })
  };

  const handleExport = async () => {
    const payload = {
      format: exportFormat,
      range: exportRange,
      columns: selectedColumns.map(col => col.value),
       selectedIds: exportRange === 'Selected' ? selectedIds : []
    };

    try {
      Swal.fire({
        title: 'Generating File...',
        text: 'Please wait while we prepare your download.',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const userToken = localStorage.getItem('userToken');
      const response = await axios.post(`${baseurl}exportUserProjects`, payload, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${userToken}` }
      });

      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const extension = exportFormat.toLowerCase() === 'xlsx' ? 'xlsx' : exportFormat.toLowerCase();
      link.setAttribute('download', `projects_export_${new Date().getTime()}.${extension}`);

      document.body.appendChild(link);
      link.click();

      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setIsOpen(false);

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Your file has been downloaded.',
        timer: 2000
      });

    } catch (error) {
      console.error("Export Error:", error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: 'There was an error generating your file. Please try again later.',
      });
    }
  };

  return (
    <>
      {/* Trigger Button */}
      <button className="action-btn" onClick={toggleModal}>
        <Download size={16} /> Export
      </button>

      {isOpen && (
        <div className="em-modal-overlay">
          <div className="em-modal-container">
            <div className="em-modal-header">
              <div className="header-title-wrapper">
                <Download size={20} className="header-icon" />
                <h2>Export Projects</h2>
              </div>
              <button className="em-close-x" onClick={toggleModal}><X size={20} /></button>
            </div>

            <div className="em-modal-body text-start">
              
              {/* Export Format */}
              <div className="em-form-group">
                <label>Export Format</label>
                <select 
                  className="em-select" 
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value)}
                >
                  <option value="CSV">CSV (Comma Separated Values)</option>
                  <option value="XLSX">Excel (.xlsx)</option>
                  <option value="JSON">JSON</option>
                </select>
              </div>

              {/* Select Range */}
              <div className="em-form-group">
                <label>Select Range</label>
                <select 
  className="em-select"
  value={exportRange}
  onChange={(e) => setExportRange(e.target.value)}
>
  <option value="All Projects">All Projects</option>
  {selectedIds.length > 0 && (
    <option value="Selected">Selected ({selectedIds.length} rows)</option>
  )}
</select>
              </div>

              {/* Columns to Include */}
              <div className="em-form-group">
                <div style={{ display: 'flex', justifyContent: 'between', alignItems: 'center', marginBottom: '4px' }}>
                  <label style={{ margin: 0 }}>Columns to Include</label>
                  {selectedColumns.length < columnOptions.length && (
                    <button 
                      type="button"
                      style={{ fontSize: '12px', color: '#4f46e5', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}
                      onClick={() => setSelectedColumns(columnOptions)}
                    >
                      (Select All)
                    </button>
                  )}
                </div>
                <Select
                  isMulti
                  options={selectOptions}
                  placeholder="Select columns..."
                  value={selectedColumns}
                  onChange={handleColumnChange}
                  menuPortalTarget={document.body} 
                  menuPosition={'fixed'} 
                  styles={{
                    ...customSelectStyles,
                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                  }}
                />
                <p className="em-help-text">Search, select individual fields, or use "Select All Columns".</p>
              </div>

              {/* Action Buttons */}
              <div className="em-modal-footer">
                <button className="em-btn-cancel" onClick={toggleModal}>Cancel</button>
                <button 
                  className="em-btn-export" 
                  onClick={handleExport}
                  disabled={selectedColumns.length === 0}
                >
                  Export Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExportProject;